/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {action} from 'mobx';
import {useLocalObservable} from 'mobx-react-lite';

import type {FormDataContextType} from 'Shared/formHelperTypes';

/**
 * Custom hook for managing global form state using MobX and local observables.
 * The `useGlobalFormData` hook initializes a local observable object that serves as a centralized
 * form data store. It provides utility methods for setting, retrieving, resetting, and deleting
 * form states by form ID. All state modifications are wrapped in MobX `action` calls to ensure
 * proper tracking and reactivity.
 * This hook is especially useful in applications with multi-step or persistent forms that need
 * to share state across components without prop drilling or excessive lifting of state.
 *
 * @returns An object containing `formsDataContext`, which includes methods to manage form state:
 * - `getFormData(formId)` – retrieves form state by ID.
 * - `setFormData(formId, formState)` – stores form state under the given ID.
 * - `resetFormData(formId)` – resets the values of a specific form.
 * - `deleteFormData(formId)` – removes a form from the store.
 *
 * @example
 * ```tsx
 * const { formsDataContext } = useGlobalFormData();
 * formsDataContext.setFormData('contactForm', someFormState);
 * const data = formsDataContext.getFormData('contactForm');
 * ```
 */
export const useGlobalFormData = () => {
    const formsDataContext = useLocalObservable<FormDataContextType>(() => ({
        forms: {},
        /**
         * Delete form data by form ID.
         * This function removes the form data associated with the given form ID from the forms object.
         * It uses MobX's action to ensure that the state change is tracked.
         *
         * @param formId The ID of the form to be deleted.
         */
        deleteFormData(formId: string) {
            action(() => {
                delete this.forms[String(formId)];
            })();
        },
        /**
         * Get form data by form ID.
         * This function retrieves the form data associated with the given form ID from the forms object.
         * If the form data does not exist, it returns null.
         *
         * @param formId The ID of the form to retrieve.
         * @returns The form data associated with the given form ID, or null if it does not exist.
         */
        getFormData(formId) {
            return this.forms[String(formId)] || null;
        },
        /**
         * Reset form data by form ID.
         * This function resets the values of the form associated with the given form ID.
         * It uses MobX's action to ensure that the state change is tracked.
         *
         * @param formId The ID of the form to be reset.
         */
        resetFormData(formId: string) {
            action(() => {
                if (this.forms[String(formId)]) {
                    this.forms[String(formId)].resetValues();
                }
            })();
        },
        /**
         * Set form data by form ID.
         * This function sets the form data for the given form ID in the forms object.
         * It uses MobX's action to ensure that the state change is tracked.
         *
         * @param formId    The ID of the form to set data for.
         * @param formState The form state to be set for the given form ID.
         */
        setFormData(formId, formState) {
            action(() => {
                this.forms[String(formId)] = formState;
            })();
        }
    }));

    return {formsDataContext};
};